import { useEffect, useRef, type RefObject } from "react";

import { isPenStrokeActive } from "./stylus-state";
import type { ViewportController } from "./viewport-controller";

/** Continuous, pointer-anchored navigation for the reader's transform model
 *  (docs/architecture/frontend/zoom-and-raster-pipeline.md). Drives a
 *  {@link ViewportController} directly — the live transform is GPU-cheap, so this updates EVERY frame
 *  without the coarse zoom stepping the old scroll-based hook needed:
 *
 *   • one finger  → pan, with flick momentum
 *   • two fingers → continuous pinch-zoom about the pinch point + pan
 *   • wheel       → pan; Ctrl/Cmd+wheel (and trackpad pinch) → zoom at cursor
 *   • keyboard    → arrows/Page pan, +/- zoom
 *
 *  It listens in the CAPTURE phase on the viewport (an ancestor of every page's
 *  editor canvas) so it sees each touch first and `stopPropagation()`s the ones
 *  it claims — the per-page Fabric editor never inks from a navigating finger.
 *
 *  Finger vs. draw: a lone finger PANS by default. It is only left for the Fabric
 *  editor to ink with when `shouldFingerDraw()` says so — i.e. finger-drawing is
 *  the intent (edit mode with palm rejection OFF). With palm rejection ON (the
 *  default, stylus expected) the very first finger contact scrolls, exactly like
 *  OneNote: the stylus draws, the hand navigates. A pen is never claimed here, so
 *  it always reaches the editor. */
export interface ViewportGestureOpts {
  /** When false the hook is inert (no navigation). Defaults to true. */
  enabled?: boolean;
  /** True when a lone finger should DRAW (be left for the editor) rather than
   *  pan — i.e. edit mode with palm rejection off. Defaults to false (finger
   *  always navigates). Read live on each gesture, so it can change per render. */
  shouldFingerDraw?: () => boolean;
  /** A two-finger TAP (both fingers down near-simultaneously, lifted quickly, with
   *  no pan/pinch movement) — wired to undo on tablets, the touch analogue of
   *  Ctrl+Z. Read live on each gesture, so the caller can gate it (edit mode). */
  onTwoFingerTap?: () => void;
  /** A three-finger TAP — wired to redo (a nice-to-have companion to the two-finger
   *  undo). Read live. */
  onThreeFingerTap?: () => void;
}

/** A single finger's lifecycle within a candidate tap (pure data for the classifier). */
export interface TapFinger {
  /** performance.now() at pointerdown. */
  downT: number;
  /** performance.now() at pointerup (0 ⇒ never lifted → not a tap). */
  upT: number;
  /** Max distance (px) the finger moved from its down position while down. */
  moved: number;
}

/** Timing / movement windows for a multi-finger tap (all optional, OneNote-ish
 *  defaults). */
export interface TapWindows {
  /** Max gap between the earliest and latest finger touching down. */
  maxSpreadMs?: number;
  /** Max time a finger may stay down (longer is a hold/drag, not a tap). */
  maxHoldMs?: number;
  /** Max movement per finger (more is a pan/pinch, not a tap). */
  maxMovePx?: number;
}

/** Classify a finished multi-finger gesture as a 2- or 3-finger TAP, or null.
 *
 *  Pure (no DOM, no timers) so the windows are unit-testable. A tap is: exactly 2
 *  (or 3) fingers, all lifted, that touched down near-simultaneously (within
 *  maxSpreadMs), each lifted quickly (within maxHoldMs of its OWN down), and none of
 *  which moved more than maxMovePx — so any real pan/pinch (movement) or hold
 *  (duration) disqualifies it. Palm-classified touches are excluded by the caller
 *  before they reach here, so they never count toward the finger total. */
export function classifyMultiFingerTap(fingers: TapFinger[], w: TapWindows = {}): 2 | 3 | null {
  const maxSpread = w.maxSpreadMs ?? 150;
  const maxHold = w.maxHoldMs ?? 250;
  const maxMove = w.maxMovePx ?? 10;
  const n = fingers.length;
  if (n !== 2 && n !== 3) return null;
  let firstDown = Infinity;
  let lastDown = -Infinity;
  for (const f of fingers) {
    if (!(f.upT > 0) || f.upT < f.downT) return null; // never lifted / bad record
    if (f.upT - f.downT > maxHold) return null; // held too long → not a tap
    if (f.moved > maxMove) return null; // moved → pan/pinch, not a tap
    if (f.downT < firstDown) firstDown = f.downT;
    if (f.downT > lastDown) lastDown = f.downT;
  }
  if (lastDown - firstDown > maxSpread) return null; // not near-simultaneous
  return n as 2 | 3;
}

type Pt = { x: number; y: number; type: string; palm: boolean };

/** ~15% per mouse-wheel notch; trackpad pinch sends many small ctrl+wheel events. */
const WHEEL_ZOOM_SENS = 0.0015;
const LINE_PX = 16; // deltaMode: lines -> px
const KEY_PAN = 80; // px per arrow press
const KEY_ZOOM = 1.15; // +/- step

export function useViewportGestures(
  viewportRef: RefObject<HTMLElement | null>,
  controllerRef: RefObject<ViewportController | null>,
  opts: ViewportGestureOpts = {},
): void {
  // Keep the latest opts (esp. shouldFingerDraw) without re-binding listeners.
  const latest = useRef(opts);
  latest.current = opts;
  const enabled = opts.enabled !== false;
  useEffect(() => {
    const el = viewportRef.current;
    const controller = controllerRef.current;
    if (!el || !controller || !enabled) return;
    return attachViewportGestures(el, controller, latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportRef, controllerRef, enabled]);
}

/** All the gesture machinery, bound to guaranteed-present element + controller.
 *  Returns a teardown. */
export function attachViewportGestures(
  el: HTMLElement,
  controller: ViewportController,
  latest: { current: ViewportGestureOpts },
): () => void {
  const pointers = new Map<number, Pt>();
  let mode: "none" | "pan" | "pinch" = "none";
  let lastCentroid = { x: 0, y: 0 };
  let lastDist = 0;
  let velocity = { x: 0, y: 0 };
  let lastMoveT = 0;
  let momentumRaf: number | null = null;
  let applyRaf: number | null = null;

  // Two/three-finger tap recognition (undo/redo on tablets). Records each non-palm
  // touch's lifecycle for the current gesture episode (first contact → all up), then
  // classifies on the final lift. A pen in the episode, a pointercancel, or any real
  // movement/hold disqualifies it (classifyMultiFingerTap owns the timing/movement
  // rules); palm-classified touches are never recorded, so they don't count.
  const tapFingers = new Map<number, { downT: number; downX: number; downY: number; upT: number; moved: number }>();
  let tapPenSeen = false;
  let tapCancelled = false;

  const rectPt = (e: PointerEvent): Pt => {
    const r = el.getBoundingClientRect();
    // A touch BORN while a pen is in contact (or mid-stroke) is the writing
    // hand's palm; mark it so it stays excluded from navigation even after the
    // pen lifts.
    const penDown = [...pointers.values()].some((p) => p.type === "pen") || isPenStrokeActive();
    return {
      x: e.clientX - r.left,
      y: e.clientY - r.top,
      type: e.pointerType,
      palm: e.pointerType === "touch" && penDown,
    };
  };
  const navTouches = () =>
    [...pointers.values()].filter((p) => p.type === "touch" && !p.palm);
  const hasPen = () => [...pointers.values()].some((p) => p.type === "pen");
  const centroid = (ts: Pt[]) =>
    ts.length >= 2
      ? { x: (ts[0]!.x + ts[1]!.x) / 2, y: (ts[0]!.y + ts[1]!.y) / 2 }
      : { x: ts[0]!.x, y: ts[0]!.y };

  const cancelMomentum = () => {
    if (momentumRaf !== null) cancelAnimationFrame(momentumRaf);
    momentumRaf = null;
  };

  /** Decide the gesture from the live pointer set and (re)seed its baseline. */
  function recompute(): void {
    const ts = navTouches();
    if (hasPen() || ts.length === 0) {
      mode = "none";
      // The finger (or the pen) is gone: drop elasticity, which springs the
      // page back inside its bounds. A live pen keeps it off entirely — the
      // paper must never move under the nib.
      controller.setElastic(false);
      return;
    }
    if (ts.length >= 2) {
      mode = "pinch";
      lastDist = Math.hypot(ts[0]!.x - ts[1]!.x, ts[0]!.y - ts[1]!.y);
      lastCentroid = centroid(ts);
    } else if (latest.current.shouldFingerDraw?.()) {
      // Finger-drawing is the intent (palm rejection off) → let the editor ink.
      mode = "none";
    } else {
      // Default: a lone finger navigates (scroll/pan), like OneNote.
      mode = "pan";
      lastCentroid = { x: ts[0]!.x, y: ts[0]!.y };
      // A live drag may pull past the clamp, with resistance. OneNote's edge.
      controller.setElastic(true);
    }
  }

  /** Coalesce multiple pointermoves in a frame into one transform update — the
   *  live transform is GPU-cheap but a per-event apply still does layout reads
   *  (getBoundingClientRect) and clamping, so throttle to rAF (as the canvas hook
   *  does). */
  const scheduleApply = () => {
    if (applyRaf !== null) return;
    applyRaf = requestAnimationFrame(() => {
      applyRaf = null;
      apply();
    });
  };

  function apply(): void {
    const ts = navTouches();
    if (mode === "pan" && ts.length >= 1) {
      const c = centroid(ts);
      const dx = c.x - lastCentroid.x;
      const dy = c.y - lastCentroid.y;
      controller.panBy(dx, dy);
      const now = performance.now();
      const dt = Math.max(1, now - lastMoveT);
      velocity = { x: 0.8 * (dx / dt) + 0.2 * velocity.x, y: 0.8 * (dy / dt) + 0.2 * velocity.y };
      lastMoveT = now;
      lastCentroid = c;
    } else if (mode === "pinch" && ts.length >= 2) {
      const c = centroid(ts);
      const d = Math.hypot(ts[0]!.x - ts[1]!.x, ts[0]!.y - ts[1]!.y);
      // Pan with the centroid, zoom continuously about it (no quantization).
      controller.panBy(c.x - lastCentroid.x, c.y - lastCentroid.y);
      if (lastDist > 0 && d > 0) controller.zoomAboutPoint(c.x, c.y, d / lastDist);
      lastDist = d;
      lastCentroid = c;
    }
  }

  function startMomentum(): void {
    const speed = Math.hypot(velocity.x, velocity.y);
    if (performance.now() - lastMoveT > 60 || speed < 0.05) return;
    const FRICTION = 0.94;
    let last = performance.now();
    const stepFn = () => {
      const now = performance.now();
      const frame = now - last;
      last = now;
      controller.panBy(velocity.x * frame, velocity.y * frame);
      const decay = Math.pow(FRICTION, frame / 16);
      velocity = { x: velocity.x * decay, y: velocity.y * decay };
      if (Math.hypot(velocity.x, velocity.y) < 0.02) {
        momentumRaf = null;
        return;
      }
      momentumRaf = requestAnimationFrame(stepFn);
    };
    momentumRaf = requestAnimationFrame(stepFn);
  }

  /** The gesture episode just ended (all pointers up). Classify the recorded fingers
   *  as a tap and fire the matching callback, then clear the episode. */
  function maybeFireTap(): void {
    const fingers = [...tapFingers.values()].map((f) => ({ downT: f.downT, upT: f.upT, moved: f.moved }));
    tapFingers.clear();
    if (tapPenSeen || tapCancelled) return;
    const res = classifyMultiFingerTap(fingers);
    if (res === 2) latest.current.onTwoFingerTap?.();
    else if (res === 3) latest.current.onThreeFingerTap?.();
  }

  const claim = (e: PointerEvent) => {
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* best effort */
    }
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
  };

  // The mouse never navigates via pointer events (it selects text / draws);
  // desktop pan+zoom is the wheel + keyboard below, and the scrollbars.
  const onDown = (e: PointerEvent) => {
    if (e.pointerType === "mouse") return;
    // A drag that starts on a scrollbar belongs to the scrollbar, not navigation.
    if ((e.target as HTMLElement | null)?.closest?.(".gv-scrollbar")) return;
    const startingEpisode = pointers.size === 0;
    const pt = rectPt(e);
    pointers.set(e.pointerId, pt);
    // Tap tracking: a fresh episode resets the records; a pen taints it (a writing
    // hand isn't tapping); each non-palm finger is recorded so the final lift can
    // classify a tap.
    if (startingEpisode) {
      tapFingers.clear();
      tapPenSeen = false;
      tapCancelled = false;
    }
    if (e.pointerType === "pen") tapPenSeen = true;
    else if (!pt.palm) {
      tapFingers.set(e.pointerId, { downT: performance.now(), downX: pt.x, downY: pt.y, upT: 0, moved: 0 });
    }
    recompute();
    if (mode !== "none") {
      cancelMomentum();
      velocity = { x: 0, y: 0 };
      lastMoveT = performance.now();
      claim(e);
    }
  };
  const onMove = (e: PointerEvent) => {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    // Preserve the birth-time palm classification; only the position moves.
    const rp = rectPt(e);
    pointers.set(e.pointerId, { ...rp, palm: prev.palm });
    // Track how far this finger has drifted from its down point — any real drift
    // disqualifies the tap (it's a pan/pinch). Done before the mode gate so a
    // finger-draw episode (mode "none") is measured too.
    const rec = tapFingers.get(e.pointerId);
    if (rec) rec.moved = Math.max(rec.moved, Math.hypot(rp.x - rec.downX, rp.y - rec.downY));
    if (mode === "none") return;
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    scheduleApply();
  };
  const onUp = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    const prevMode = mode;
    pointers.delete(e.pointerId);
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    // Tap tracking: a cancel (system gesture cut in) disqualifies; record the lift,
    // and once the last pointer is up, classify the episode.
    if (e.type === "pointercancel") tapCancelled = true;
    const rec = tapFingers.get(e.pointerId);
    if (rec && rec.upT === 0) rec.upT = performance.now();
    if (pointers.size === 0) maybeFireTap();
    recompute();
    if (prevMode === "pan" && mode === "none") startMomentum();
  };

  // Wheel: trackpad two-finger scroll pans; Ctrl/Cmd+wheel (and the trackpad
  // pinch gesture, which the browser reports as ctrl+wheel) zooms at the cursor.
  const onWheel = (e: WheelEvent) => {
    const r = el.getBoundingClientRect();
    const px = e.clientX - r.left;
    const py = e.clientY - r.top;
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      controller.zoomAboutPoint(px, py, Math.exp(-e.deltaY * WHEEL_ZOOM_SENS));
      return;
    }
    const k = e.deltaMode === 1 ? LINE_PX : e.deltaMode === 2 ? el.clientHeight : 1;
    controller.panBy(-e.deltaX * k, -e.deltaY * k);
  };

  const onKey = (e: KeyboardEvent) => {
    let handled = true;
    switch (e.key) {
      case "ArrowLeft": controller.panBy(KEY_PAN, 0); break;
      case "ArrowRight": controller.panBy(-KEY_PAN, 0); break;
      case "ArrowUp": controller.panBy(0, KEY_PAN); break;
      case "ArrowDown": controller.panBy(0, -KEY_PAN); break;
      case "PageUp": controller.panBy(0, el.clientHeight * 0.9); break;
      case "PageDown": case " ": controller.panBy(0, -el.clientHeight * 0.9); break;
      case "+": case "=": controller.zoomTo(controller.scale * KEY_ZOOM); break;
      case "-": case "_": controller.zoomTo(controller.scale / KEY_ZOOM); break;
      default: handled = false;
    }
    if (handled) e.preventDefault();
  };

  // WebKit's own pinch-zoom of the PAGE. `touch-action: none` does not stop it:
  // Safari reports a pinch through the non-standard gesture events, and those are
  // the only place to refuse it. Left alone, a pinch that starts anywhere outside
  // the surface — the toolbar, the margin — scales the whole document. Every
  // client rect the surface measures is then in a different space from the touch
  // coordinates, so pan, pinch and the pen all stop responding, and nothing but a
  // reload recovers. This app owns zoom on these surfaces, so refuse the browser's.
  //
  // Bound on the DOCUMENT, not on `el`: a pinch that begins on the toolbar has to
  // be refused too, and that is where the reported lock-up came from.
  const onGesture = (e: Event) => {
    if (e.cancelable) e.preventDefault();
  };
  document.addEventListener("gesturestart", onGesture, { passive: false });
  document.addEventListener("gesturechange", onGesture, { passive: false });
  document.addEventListener("gestureend", onGesture, { passive: false });

  el.addEventListener("pointerdown", onDown, true);
  el.addEventListener("pointermove", onMove, true);
  el.addEventListener("pointerup", onUp, true);
  el.addEventListener("pointercancel", onUp, true);
  el.addEventListener("wheel", onWheel, { passive: false });
  el.addEventListener("keydown", onKey);
  return () => {
    cancelMomentum();
    if (applyRaf !== null) cancelAnimationFrame(applyRaf);
    el.removeEventListener("pointerdown", onDown, true);
    el.removeEventListener("pointermove", onMove, true);
    el.removeEventListener("pointerup", onUp, true);
    el.removeEventListener("pointercancel", onUp, true);
    el.removeEventListener("wheel", onWheel);
    el.removeEventListener("keydown", onKey);
    document.removeEventListener("gesturestart", onGesture);
    document.removeEventListener("gesturechange", onGesture);
    document.removeEventListener("gestureend", onGesture);
  };
}
